import React, { KeyboardEvent, useMemo } from 'react';
import { 
  Drawer, 
  Box, 
  List, 
  ListItem, 
  ListItemButton, 
  ListItemIcon, 
  ListItemText, 
  Toolbar, 
  Divider,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
  Badge,
  Switch,
  Typography,
  FormControlLabel
} from '@mui/material';
import { useNavigate, useLocation, matchPath } from 'react-router-dom';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import navigationItems from '../../shared/config/navigation';
import { useCompanyPlan } from '@/shared/context';

interface SidebarProps {
  drawerWidth: number;
  collapsedWidth: number;
  isCollapsed: boolean;
  mobileOpen: boolean;
  handleDrawerToggle: () => void;
  handleCollapseToggle: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ 
  drawerWidth,
  collapsedWidth,
  isCollapsed,
  mobileOpen,
  handleDrawerToggle,
  handleCollapseToggle
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // Changed to 'sm' (600px)
  
  // Get plan info from context
  const { isBasicPlan, viewMode, setViewModeOverride } = useCompanyPlan();
  const isProMode = viewMode === 'pro';
  
  // Filter navigation items based on plan
  const filteredNavItems = useMemo(() => {
    return navigationItems.filter(item => {
      // If item is proOnly and user is on BASIC plan, hide it
      if (item.proOnly && isBasicPlan) {
        return false;
      }
      return true;
    });
  }, [isBasicPlan]);
  
  const currentWidth = isCollapsed ? collapsedWidth : drawerWidth;

  // Check if the current route matches a navigation item
  const isPathActive = (path: string): boolean => {
    // Special handling for facturas path to match sub-routes
    if (path === '/facturas') {
      return location.pathname === '/facturas' || location.pathname.startsWith('/facturas/');
    }
    return !!matchPath({ path, end: true }, location.pathname);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>, path: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      navigate(path);
      if (isMobile) {
        handleDrawerToggle();
      }
    }
  };
  
  // Handle view mode toggle
  const handleViewModeToggle = (checked: boolean) => {
    setViewModeOverride(checked ? 'pro' : 'basic');
  };

  const drawer = (
    <>
      <Toolbar sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: isCollapsed ? 'center' : 'flex-end',
        px: isCollapsed ? 1 : 1
      }}>
        {!isMobile && (
          <Tooltip title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}>
            <IconButton 
              onClick={handleCollapseToggle} 
              aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {isCollapsed ? 
                (theme.direction === 'rtl' ? <ChevronLeftIcon /> : <ChevronRightIcon />) : 
                (theme.direction === 'rtl' ? <ChevronRightIcon /> : <ChevronLeftIcon />)
              }
            </IconButton>
          </Tooltip>
        )}
      </Toolbar>
      <Divider />
      <List role="menu" aria-label="Navigation principal">
        {filteredNavItems.map((item) => {
          // Check if this path is active
          const isActive = isPathActive(item.path);
          
          // Generate IconComponent from the icon prop
          const IconComponent = item.icon;

          return (
            <ListItem key={item.text} disablePadding>
              <Tooltip title={isCollapsed ? item.text : ""} placement="right">
                <ListItemButton 
                  selected={isActive}
                  onClick={() => {
                    navigate(item.path);
                    if (isMobile) {
                      handleDrawerToggle();
                    }
                  }}
                  onKeyDown={(e) => handleKeyDown(e, item.path)}
                  sx={{
                    minHeight: 48,
                    justifyContent: isCollapsed ? 'center' : 'initial',
                    px: 2.5,
                  }}
                  role="menuitem"
                  aria-current={isActive ? 'page' : undefined}
                  aria-label={item.ariaLabel || item.text}
                  tabIndex={0}
                >
                  <ListItemIcon sx={{ 
                    color: isActive ? 'primary.main' : 'inherit',
                    minWidth: 0,
                    mr: isCollapsed ? 'auto' : 3,
                    justifyContent: 'center',
                  }}>
                    {item.badge ? (
                      <Badge 
                        badgeContent={item.badge.content} 
                        color={item.badge.color || 'primary'}
                        sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem' } }}
                      >
                        <IconComponent />
                      </Badge>
                    ) : (
                      <IconComponent />
                    )}
                  </ListItemIcon>
                  {!isCollapsed && (
                    <ListItemText primary={item.text} />
                  )}
                </ListItemButton>
              </Tooltip>
            </ListItem>
          );
        })}
      </List>
      
      {/* View Mode Toggle at bottom - only show for PRO plan users */}
      {!isBasicPlan && (
        <>
          <Box sx={{ flexGrow: 1 }} />
          <Divider />
          <Box sx={{ 
            p: isCollapsed ? 1 : 2, 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: isCollapsed ? 'center' : 'flex-start'
          }}>
            {isCollapsed ? (
              <Tooltip title={isProMode ? "Modo Pro" : "Modo Básico"} placement="right">
                <Switch
                  checked={isProMode}
                  onChange={(e) => handleViewModeToggle(e.target.checked)}
                  size="small"
                  color="primary"
                />
              </Tooltip>
            ) : (
              <>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
                  Modo de vista
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isProMode}
                      onChange={(e) => handleViewModeToggle(e.target.checked)}
                      size="small"
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {isProMode ? 'Pro' : 'Básico'}
                    </Typography>
                  }
                  sx={{ m: 0 }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, fontSize: '0.65rem' }}>
                  {isProMode ? 'Con AFIP y más opciones' : 'Simplificado para kioscos'}
                </Typography>
              </>
            )}
          </Box>
        </>
      )}
    </>
  );

  return (
    <Box
      component="nav"
      sx={{ width: { sm: currentWidth }, flexShrink: { sm: 0 } }}
      aria-label="navigation"
      data-testid="sidebar-container"
    >
      {/* Mobile drawer - shows on screens less than 600px */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile
        }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: drawerWidth,
            overflowX: 'hidden'
          },
        }}
        data-testid="mobile-sidebar"
      >
        {drawer}
      </Drawer>
      
      {/* Desktop drawer - shows on screens 600px and above */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', sm: 'block' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: currentWidth,
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            overflowX: 'hidden'
          },
        }}
        open
        data-testid="desktop-sidebar"
      >
        {drawer}
      </Drawer>
    </Box>
  );
};

export default Sidebar;
